'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useState } from 'react';
import clsx from 'clsx';

import { DealerItem, MapLocation } from '@/slw/entities/buyer/available-dealer/available-dealer.type';
import { CustomSelect } from '@/slw/shared/ui/custom/select';

type Center = [number, number];

interface MapBoxFilterProps {
  allDealer: DealerItem[];
}

// Dynamically import the Map component to avoid SSR issues with Leaflet
const MapComponent = dynamic(() => import('@/slw/shared/ui/map').then((mod) => mod.MapComponent), {
  ssr: false,
  loading: () => (
    <div className='w-full h-[350px] md:h-[500px] flex items-center justify-center bg-muted'>
      <div className='text-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto'></div>
        <p className='mt-4 text-lg'>Loading map...</p>
      </div>
    </div>
  ),
});

const renderContent = (location: MapLocation) => {
  return (
    <>
      {location.email && <div className='mb-1 text-[0.875rem]'>{location.email}</div>}
      {location.telephone && <div className='mb-1 text-[0.875rem]'>{location.telephone}</div>}
      {location.dealerListings && (
        <Link href={location.dealerListings} className='mb-1 text-[0.875rem] text-primary!'>
          Dealer&apos;s listings
        </Link>
      )}
    </>
  );
};

const defaultCenter: Center = [39.2, -84.51];

export function MapBoxFilter({ allDealer = [] }: MapBoxFilterProps) {
  const [selectedDealer, setSelectedDealer] = useState('');
  const [center, setCenter] = useState<Center>([...defaultCenter]);
  const [zoom, setZoom] = useState<number>(5);

  const dropdownOptions = [
    {
      value: '0',
      label: 'All Dealers',
    },
  ];
  const locations: MapLocation[] = [];

  for (const dealer of allDealer) {
    dropdownOptions.push({
      value: dealer.id,
      label: dealer.fullName,
    });

    locations.push({
      id: dealer.id,
      name: dealer.fullName,
      position: [Number(dealer.latitude), Number(dealer.longitude)],
      category: 'restaurant',
      email: dealer.email,
      telephone: dealer.preferredContactPhoneNumber,
      dealerListings: `/dealer-listings/${dealer.id}`,
    });
  }

  const onChangeDealer = (id: string) => {
    let newCenter: Center = [...defaultCenter];

    if (id !== '0') {
      setZoom(5);
      const location = locations.find((item) => item.id === id);

      if (location?.position) {
        newCenter = location.position;
      }
    } else {
      setZoom(2);
    }

    setCenter(newCenter);
    setSelectedDealer(id);
  };

  return (
    <div
      className='relative mt-3 md:-mt-[150px] lg:-mt-[250px] xl:-mt-[300px] 2xl:-mt-[300px] w-full bg-[#F8F8F7]
      p-[0.875rem] md:p-[1rem] lg:p-[1.5rem] rounded-md'
    >
      <div className='wrap flex-col md:flex-row flex gap-[1.5rem]'>
        <div>
          <CustomSelect className='mb-3' options={dropdownOptions} value={selectedDealer} onChange={onChangeDealer} />
          <div
            className='border border-[#EBE9E5] bg-[#F5ECDD]/30 rounded-md w-full md:w-[220px] lg:w-[290px] p-[10px_24px]
        overflow-auto max-h-[438px]'
          >
            {allDealer.map((item) => (
              <div key={item.id}>
                <button
                  type='button'
                  className={clsx('px-0 py-2 border-none outline-none text-[0.875rem] font-[500]', {
                    'font-bold': selectedDealer === item.id,
                  })}
                  onClick={() => onChangeDealer(item.id)}
                >
                  {item.fullName}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className='flex-1 relative h-[350px] md:h-[500px] overflow-hidden rounded-md z-[1]'>
          <MapComponent
            locations={locations}
            renderCustomContent={renderContent}
            mapConfig={{ center, zoom, maxZoom: 19, minZoom: 1 }}
            className='h-[350px] md:h-[500px]'
          />
        </div>
      </div>
    </div>
  );
}
